import { api } from "./axiosClient";
import type { NativeSigningSendOptions } from "./nativeSigningApi";

export interface SigningProviderOption {
  name: string;
  displayName: string;
  isDefault: boolean;
  /** Signers sign inside Pactline (Pactline Native Signature): fields are placed before sending. */
  signsInApp?: boolean;
  /** Native only: the signature methods the organisation allows. */
  methods?: { draw: boolean; upload: boolean; type: boolean; saved: boolean } | null;
}

export const contractWorkflowApi = {
  sendForApproval: async (
    contractId: string,
    docxS3Key: string,
    docxFileName: string,
    subject: string,
    body: string,
  ) => {
    return await api.post("/contract/sendforapproval", {
      contractId,
      docxS3Key,
      docxFileName,
      subject,
      body,
    });
  },

  sendToReviewer: async (
    contractId: string,
    docxS3Key: string,
    docxFileName: string,
    subject: string,
    body: string,
  ) => {
    return await api.post("/contract/sendtoreviewer", {
      contractId,
      docxS3Key,
      docxFileName,
      subject,
      body,
    });
  },

  sendForExternalReview: async (
    contractId: string,
    docxS3Key: string,
    docxFileName: string,
    externalEmails: string[],
    ccEmails: string[],
    subject: string,
    body: string,
    // Extra Documents-tab files; the contract snapshot above is always sent.
    attachments: { s3Key: string; fileName: string }[] = [],
  ) => {
    return await api.post("/contract/sendforexternalreview", {
      contractId,
      docxS3Key,
      docxFileName,
      externalEmails,
      ccEmails,
      subject,
      body,
      attachments,
    });
  },

  sendToEditor: async (
    contractId: string,
    docxS3Key: string,
    docxFileName: string,
    subject: string,
    body: string,
  ) => {
    return await api.post("/contract/sendtoeditor", {
      contractId,
      docxS3Key,
      docxFileName,
      subject,
      body,
    });
  },

  sendToRepository: async (
    contractId: string,
    docxS3Key: string,
    docxFileName: string,
  ) => {
    return await api.post("/contract/sendtorepository", {
      contractId,
      docxS3Key,
      docxFileName,
    });
  },

  sendForSignature: async (
    contractId: string,
    docxS3Key: string,
    docxFileName: string,
    recipients: Array<{ name: string; email: string }>,
    signatureProvider?: string,
    nativeSigning?: NativeSigningSendOptions,
  ) => {
    return await api.post("/contract/sendforsignature", {
      contractId,
      docxS3Key,
      docxFileName,
      recipients,
      signatureProvider,
      nativeSigning,
    });
  },

  // Signing providers with credentials configured on the server — the list
  // the user picks from before sending for signature.
  getSigningProviders: async (): Promise<SigningProviderOption[]> => {
    const res = await api.get("/contract/signingproviders");
    return res.data ?? [];
  },

  // decisionType: 1=Approved, 2=ApprovedWithComments, 3=ChangesRequired, 4=NotApproved
  // — see DecisionType in ../utils/contractStatus.
  submitApprovalDecision: async (
    contractId: string,
    decisionType: number,
    comment: string,
  ) => {
    return await api.post("/contract/submitdecision", {
      contractId,
      decisionType,
      comment,
    });
  },

  completeReview: async (contractId: string, comment: string) => {
    return await api.post("/contract/completereview", {
      contractId,
      comment,
    });
  },
};
